import React, { useState, useMemo } from 'react';
import {
  Sparkles, Lightbulb, UploadCloud, FileCheck2, AlertCircle, CheckCircle2, Calculator,
} from 'lucide-react';
import StatusBadge from './StatusBadge';
import { useHGS } from '../context/HGSContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Renders the "answer form" for a single master row (one question/control/declaration).
 * Driven by the same field-type vocabulary as the canonical schema:
 *   text | textarea | dropdown | radio | date | number | file_upload | calculated | ai_guidance | status_badge
 *
 * Props:
 *   questionRow  – the master row (e.g. { questionId, complianceRequirement, responseType, ... , active })
 *   schema       – MODULE_SCHEMAS entry for this module (drives field types/options/showWhen)
 *   index        – display index
 *   answerFieldKeys – which schema keys are answerable by the end user (rest is meta/config)
 *   questionTextKey – which schema key holds the question text to display
 *   answerStateKey  – which schema key holds the user's response
 */

// ────────────────────────────────────────────────────────────────────────────
// Field renderers — one per type. Each is a pure function that takes the same
// props as FieldControl and returns JSX. The registry below dispatches.
// ────────────────────────────────────────────────────────────────────────────

const renderTextarea = ({ value, onChange, field, base }) => (
  <textarea
    rows={3}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.sampleValue || ''}
    className={cn(base, 'resize-y')}
  />
);

const renderDropdown = ({ value, onChange, field, base }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={base}>
    <option value="">— Select —</option>
    {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const RADIO_TONES = {
  Yes:    'bg-emerald-600 text-white border-emerald-600',
  OK:     'bg-emerald-600 text-white border-emerald-600',
  No:     'bg-[#CC0000] text-white border-[#CC0000]',
  'N/G':  'bg-[#CC0000] text-white border-[#CC0000]',
  'N/A':  'bg-slate-700 text-white border-slate-700',
};

const renderRadio = ({ value, onChange, field, recordId }) => (
  <div className="flex flex-wrap gap-1.5">
    {(field.options || []).map((o) => {
      const sel = value === o;
      const tone = RADIO_TONES[o] || 'bg-[#CC0000] text-white border-[#CC0000]';
      return (
        <button
          key={o}
          type="button"
          data-testid={`radio-${field.key || field.id || field.name}-${o.replace(/[^a-zA-Z0-9]/g, '')}-${recordId || ''}`}
          onClick={() => onChange(o)}
          className={cn(
            'px-3 py-1.5 rounded-md border text-[11px] font-bold transition',
            sel ? tone : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
          )}
        >
          {o}
        </button>
      );
    })}
  </div>
);

const renderDate = ({ value, onChange, base }) =>
  <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className={base} />;

const renderNumber = ({ value, onChange, base }) =>
  <input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)} className={base} />;

const renderFileUpload = ({ value, onChange }) => {
  const fileName = value?.name || value || '';
  return (
    <label className="flex items-center gap-3 px-3 py-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/40 hover:border-[#CC0000] hover:bg-red-50/30 cursor-pointer transition">
      {fileName ? (
        <>
          <FileCheck2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-[11px] text-slate-700 truncate flex-1 font-mono">{fileName}</span>
          <span className="text-[10px] font-semibold text-emerald-700">Uploaded</span>
        </>
      ) : (
        <>
          <UploadCloud className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <span className="text-[11px] text-slate-600 flex-1">Upload evidence</span>
          <span className="text-[10px] font-semibold text-[#CC0000]">Browse</span>
        </>
      )}
      <input
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.docx,.doc"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange(f.name); }}
      />
    </label>
  );
};

// Calculated field — only the demo formula `frequency*businessImpact` is supported.
const FREQ_MAP = { Rare: 1, Unlikely: 2, Possible: 3, Likely: 4, 'Almost Certain': 5 };
const IMP_MAP  = { Insignificant: 1, Minor: 2, Moderate: 3, Major: 4, Catastrophic: 5 };

function computeRiskScore(field, allValues) {
  let label = field.sampleValue || 'Calculated';
  let detail = field.formula || '—';
  let band = null;
  if (field.formula === 'frequency*businessImpact') {
    const f = FREQ_MAP[allValues.frequency] || 0;
    const i = IMP_MAP[allValues.businessImpact] || 0;
    const score = f * i;
    if (score) {
      label = `${score} / 25`;
      detail = `${allValues.frequency} (${f}) × ${allValues.businessImpact} (${i})`;
      band = score >= 17 ? 'Critical' : score >= 10 ? 'High' : score >= 5 ? 'Medium' : 'Low';
    } else {
      label = 'Pending — choose Frequency × Impact';
      detail = 'Auto-calculated risk score';
    }
  }
  return { label, detail, band };
}

const renderCalculated = ({ field, allValues }) => {
  const { label, detail, band } = computeRiskScore(field, allValues);
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200">
      <Calculator className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold text-amber-800">{label}</div>
        {detail && <div className="text-[9px] text-amber-700/80 truncate">{detail}</div>}
      </div>
      {band && <StatusBadge status={band} dot={false} />}
    </div>
  );
};

const renderStatusBadge = ({ value, onChange, field }) => {
  const options = field.options && field.options.length ? field.options : ['Low', 'Medium', 'High', 'Critical'];
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const sel = value === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={cn('transition', sel ? 'ring-2 ring-offset-1 ring-[#CC0000] rounded-full' : 'opacity-60 hover:opacity-100')}
          >
            <StatusBadge status={o} />
          </button>
        );
      })}
    </div>
  );
};

const renderAIGuidance = ({ value, onChange, field }) => (
  <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50/40 p-3">
    <div className="flex items-center gap-1.5 mb-2">
      <Sparkles className="w-3.5 h-3.5 text-blue-700" />
      <span className="text-[10px] font-bold text-blue-900 uppercase tracking-wider">AI Guidance</span>
    </div>
    {field.aiExamples && field.aiExamples.length > 0 ? (
      <ul className="space-y-1">
        {field.aiExamples.map((ex, i) => (
          <li key={`ai-${i}-${(ex || '').slice(0, 20)}`} className="flex items-start gap-2 text-[11px] text-slate-700 leading-relaxed">
            <Lightbulb className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
            <span>{ex}</span>
          </li>
        ))}
      </ul>
    ) : null}
    <textarea
      rows={2}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Add your own examples…"
      className="mt-2 w-full px-2 py-1.5 text-[11px] bg-white border border-blue-200 rounded-md focus:outline-none focus:border-blue-500 resize-y"
    />
  </div>
);

const renderText = ({ value, onChange, field, base }) => (
  <input
    type="text"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={field.sampleValue || ''}
    className={base}
  />
);

const FIELD_RENDERERS = {
  textarea:     renderTextarea,
  dropdown:     renderDropdown,
  radio:        renderRadio,
  date:         renderDate,
  number:       renderNumber,
  file_upload:  renderFileUpload,
  calculated:   renderCalculated,
  status_badge: renderStatusBadge,
  ai_guidance:  renderAIGuidance,
  text:         renderText,
};

function FieldControl({ field, value, onChange, allValues, error, recordId }) {
  const base = cn(
    'w-full px-3 py-2 text-xs bg-white border rounded-lg focus:outline-none focus:ring-2 transition',
    error
      ? 'border-[#CC0000] focus:border-[#CC0000] focus:ring-[#CC0000]/15'
      : 'border-slate-300 focus:border-[#CC0000] focus:ring-[#CC0000]/10 text-slate-800'
  );
  const renderer = FIELD_RENDERERS[field.type] || renderText;
  return renderer({ field, value, onChange, allValues, error, base, recordId });
}

function isFieldVisible(field, allValues) {
  if (!field.showWhen) return true;
  const { fieldKey, valueIn, equals } = field.showWhen;
  const v = allValues[fieldKey];
  if (Array.isArray(valueIn)) return valueIn.includes(v);
  if (equals != null) return v === equals;
  return true;
}

// Maps schema field "key" → renderer type (mirrors masterData.js toRenderType)
function toRenderType(f) {
  if (f.renderAs) return f.renderAs;
  if (f.key === 'aiGuidanceExamples') return 'ai_guidance';
  if (f.computed) return 'calculated';
  if (f.type === 'longtext') return 'textarea';
  if (f.type === 'date') return 'date';
  if (f.type === 'number') return 'number';
  if (f.type === 'file') return 'file_upload';
  if (f.type === 'status_badge') return 'status_badge';
  if (f.options) return 'dropdown';
  return 'text';
}

export default function SchemaQuestionCard({
  schema,
  questionRow,
  index,
  questionTextKey,
  answerableKeys,
  initialValues = {},
  accent = '#CC0000',
  readOnly = false,
}) {
  const { submitFormResponse, formResponses, masterMeta } = useHGS();
  const moduleKey = schema.key;
  const recordId = questionRow[schema.idField];
  const responseKey = `${moduleKey}:${recordId}`;
  const persisted = formResponses?.[responseKey];

  const sourceFile = questionRow._sourceFile || masterMeta?.[moduleKey]?.fileName;
  const sourceSheet = questionRow._sourceSheet;
  const sourceRow = questionRow._sourceRow;

  const [values, setValues] = useState(() => ({ ...initialValues, ...(persisted?.values || {}) }));
  const [submitted, setSubmitted] = useState(!!persisted);

  const fields = useMemo(() => {
    return schema.fields
      .filter((f) => answerableKeys.includes(f.key))
      .map((f) => ({ ...f, fieldKey: f.key, label: f.label, type: toRenderType(f), required: !!f.mandatory }));
  }, [schema, answerableKeys]);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  // Required fields are checked only AFTER the user attempts submit; future-date
  // is enforced immediately so the user sees the error the moment they pick
  // today/past.
  const errors = useMemo(() => {
    const errs = {};
    fields.forEach((f) => {
      if (!isFieldVisible(f, values)) return;
      const v = values[f.fieldKey];
      if (submitted && f.required && f.type !== 'calculated' && f.type !== 'ai_guidance') {
        if (v == null || (typeof v === 'string' && !v.trim())) errs[f.fieldKey] = 'Required';
      }
      if (f.validate === 'futureDate' && v && typeof v === 'string') {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const picked = new Date(v);
        if (!isNaN(picked.getTime()) && picked.getTime() <= today.getTime()) {
          errs[f.fieldKey] = 'Date of Completion must be a future date (greater than today).';
        }
      }
    });
    return errs;
  }, [submitted, fields, values]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setSubmitted(true);
    // Re-run validation including required-field gates (since `errors` only
    // gates required-fields after submitted, we need a fresh pass here).
    const errs = {};
    fields.forEach((f) => {
      if (!isFieldVisible(f, values)) return;
      const v = values[f.fieldKey];
      if (f.required && f.type !== 'calculated' && f.type !== 'ai_guidance') {
        if (v == null || (typeof v === 'string' && !v.trim())) errs[f.fieldKey] = 'Required';
      }
      if (f.validate === 'futureDate' && v && typeof v === 'string') {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const picked = new Date(v);
        if (!isNaN(picked.getTime()) && picked.getTime() <= today.getTime()) {
          errs[f.fieldKey] = 'Date of Completion must be a future date (greater than today).';
        }
      }
    });
    if (Object.keys(errs).length === 0) {
      submitFormResponse(moduleKey, recordId, values);
      toast.success('Response saved', { description: `${recordId} · dashboard KPIs updating live` });
    } else {
      toast.error('Please complete required fields', { description: `${Object.keys(errs).length} field(s) missing` });
    }
  };

  const responseValue = values.responseType || values.degreeOfRisk || '';
  const headerColor = (responseValue === 'No' || responseValue === 'N/G') ? '#CC0000'
    : responseValue === 'N/A' ? '#475569'
    : (responseValue === 'Yes' || responseValue === 'OK') ? '#059669'
    : accent;

  return (
    <div
      className={cn(
        'rounded-xl border bg-white overflow-hidden transition',
        questionRow.active === false ? 'opacity-60' : ''
      )}
      style={{ borderColor: headerColor + '40' }}
      data-testid={`schema-question-${index}`}
    >
      <div className="flex items-start gap-3 p-5">
        <span
          className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white"
          style={{ backgroundColor: headerColor }}
        >
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[10px] text-slate-500">{questionRow[schema.idField]}</span>
            {questionRow.active === false && <StatusBadge status="Inactive" />}
            {responseValue && (
              <span
                className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                style={{ backgroundColor: headerColor + '15', color: headerColor }}
              >
                {responseValue}
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-slate-900 leading-relaxed">
            {questionRow[questionTextKey] || '—'}
          </div>
          {/* DLC: expandable Required Activities list (sub-steps that the
              department must perform to satisfy the parent check point) */}
          {moduleKey === 'dlc' && Array.isArray(questionRow.requiredActivities) && questionRow.requiredActivities.length > 0 && (
            <DlcRequiredActivities activities={questionRow.requiredActivities} />
          )}
          {sourceFile && (
            <div className="mt-1.5 inline-flex flex-wrap items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-[9px] font-medium text-slate-600" data-testid={`source-badge-${recordId}`}>
              <span className="font-bold text-slate-500 uppercase tracking-wider">Source:</span>
              <span>Official HMSI Master</span>
              <span className="text-slate-400">·</span>
              <span className="font-mono truncate max-w-[260px]" title={sourceFile}>{sourceFile}</span>
              {sourceSheet && (<>
                <span className="text-slate-400">·</span>
                <span>Sheet: <span className="font-mono font-bold text-slate-700">{sourceSheet}</span></span>
              </>)}
              {sourceRow != null && (<>
                <span className="text-slate-400">·</span>
                <span>Row: <span className="font-mono font-bold text-slate-700">{sourceRow}</span></span>
              </>)}
            </div>
          )}
        </div>
      </div>

      <div className="px-5 pb-5 pt-2 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100">
        {fields.map((f) => {
          if (!isFieldVisible(f, values)) return null;
          const fullWidth = f.type === 'textarea' || f.type === 'ai_guidance' || f.type === 'file_upload';
          const err = errors[f.fieldKey];
          return (
            <div
              key={f.fieldKey}
              className={cn(fullWidth ? 'md:col-span-2' : '', 'animate-in fade-in slide-in-from-top-1 duration-200')}
            >
              <label className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                {f.label}
                {f.required && <span className="text-[#CC0000]">*</span>}
                {f.showWhen && (
                  <span className="ml-1.5 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[8px] font-bold">
                    CONDITIONAL
                  </span>
                )}
              </label>
              {readOnly ? (
                <div className="relative">
                  <div className="pointer-events-none opacity-75">
                    <FieldControl field={f} value={values[f.fieldKey]} onChange={() => {}} allValues={values} error={err} recordId={recordId} />
                  </div>
                  <div className="absolute inset-0 rounded-lg bg-slate-100/20 ring-1 ring-slate-200 ring-inset" aria-hidden="true" />
                </div>
              ) : (
                <FieldControl field={f} value={values[f.fieldKey]} onChange={(v) => setVal(f.fieldKey, v)} allValues={values} error={err} recordId={recordId} />
              )}
              {f.helpText && !err && (
                <p className="mt-1 text-[10px] text-slate-500 leading-relaxed">{f.helpText}</p>
              )}
              {err && (
                <p className="mt-1 text-[10px] text-[#CC0000] flex items-center gap-1 font-medium">
                  <AlertCircle className="w-3 h-3" />
                  {err}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-5 py-3 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between">
        <div className="text-[10px] text-slate-500 flex items-center gap-2">
          Schema-driven · {fields.length} fields rendered from master
          {persisted && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[9px] uppercase">
              <CheckCircle2 className="w-2.5 h-2.5" /> Submitted
            </span>
          )}
          {readOnly && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-bold text-[9px] uppercase">
              Read-only
            </span>
          )}
        </div>
        {readOnly ? (
          <span className="text-[10px] font-bold text-slate-500 italic" data-testid={`readonly-${recordId}`}>
            Review role — input is locked
          </span>
        ) : (
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-semibold rounded-md"
          data-testid={`save-response-${recordId}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          {persisted ? 'Update response' : 'Save response'}
        </button>
        )}
      </div>

      {persisted && (
        <AuditTrailStrip persisted={persisted} recordId={recordId} />
      )}
    </div>
  );
}

function AuditTrailStrip({ persisted, recordId }) {
  const history = persisted.history || [];
  // Derive submitter/reviewer/approver rows
  const submitter = history.find((h) => /Submit/i.test(h.action)) || {
    action: 'Submitted', by: persisted.submittedBy, role: persisted.submittedRole, at: persisted.submittedAt,
  };
  const reviewer = history.find((h) => /Division/i.test(h.action) || /Review/i.test(h.action));
  const approver = history.find((h) => /Final/i.test(h.action) || h.action === 'Approved');
  const rejected = history.find((h) => /Reject/i.test(h.action));

  const fmt = (ts) => ts ? new Date(ts).toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="px-5 py-3 bg-gradient-to-r from-slate-50 to-white border-t border-slate-100" data-testid={`audit-trail-${recordId}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Audit trail</span>
        <span className="text-[9px] text-slate-400">· Workflow status:</span>
        <span className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded uppercase',
          persisted.workflowStatus === 'FinalApproved' ? 'bg-emerald-500 text-white' :
          persisted.workflowStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
          persisted.workflowStatus === 'Approved' ? 'bg-emerald-100 text-emerald-800' :
          persisted.workflowStatus === 'UnderReview' ? 'bg-amber-100 text-amber-800' :
          'bg-blue-100 text-blue-800'
        )}>
          {persisted.workflowStatus || 'Submitted'}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-[10px]">
        <AuditCell label="Submitted" name={submitter?.by || '—'} role={submitter?.role} at={fmt(submitter?.at)} active />
        <AuditCell label="Reviewed" name={reviewer?.by || '—'} role="Division Head" at={fmt(reviewer?.at)} active={!!reviewer} />
        <AuditCell label={rejected ? 'Rejected' : 'Approved'} name={(approver || rejected)?.by || '—'} role="Operating Head" at={fmt((approver || rejected)?.at)} active={!!(approver || rejected)} rejected={!!rejected} />
      </div>
    </div>
  );
}

function AuditCell({ label, name, role, at, active, rejected }) {
  return (
    <div className={cn(
      'p-2 rounded-md border',
      active && !rejected && 'bg-emerald-50 border-emerald-200',
      active && rejected && 'bg-red-50 border-red-200',
      !active && 'bg-white border-slate-200 opacity-60'
    )}>
      <div className={cn('font-bold text-[9px] uppercase tracking-wider',
        active && !rejected && 'text-emerald-700',
        active && rejected && 'text-red-700',
        !active && 'text-slate-400'
      )}>{label}</div>
      <div className="text-[11px] font-semibold text-slate-900 truncate">{name}</div>
      <div className="text-[9px] text-slate-500">{role || '—'} · {at}</div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// DLC Required Activities — expandable sub-step list. For DLC parent items,
// the official HMSI master breaks each Check Point down into 1+ sub-activities
// the division must perform. We surface them inline as a collapsible list so
// the Department Head can see exactly what underpins the OK / N/G / N/A call.
// ────────────────────────────────────────────────────────────────────────────
function DlcRequiredActivities({ activities }) {
  const [open, setOpen] = useState(false);
  if (!activities?.length) return null;
  return (
    <div className="mt-2" data-testid="dlc-required-activities">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 hover:text-violet-900 transition"
      >
        <span className={cn('inline-block transition-transform', open && 'rotate-90')}>▸</span>
        Required Activities ({activities.length})
      </button>
      {open && (
        <ol className="mt-2 ml-1 pl-3 border-l-2 border-violet-200 space-y-1.5">
          {activities.map((a, i) => (
            <li key={a.activityId || i} className="text-[11px] leading-snug text-slate-700">
              <span className="font-mono font-bold text-violet-700 mr-1">{a.activityId || `${i + 1}.`}</span>
              {a.activity}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
